import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import TenantDetailView from './TenantDetailView';
import '../../styles/components/admin/TenantManagement.css';
import SearchIcon from '../../assets/icons/search_icon.svg';
import NeuroLexLogo from '../../assets/images/neurolex_logo_dropshadow.png';
import NeuroLexLogo2 from '../../assets/images/Neurolex_Logo_New.png';
import TenantSetupIcon from '../../assets/icons/indicators/ProfessionalInfo_icon.png';
import ArchiveIcon from '../../assets/icons/indicators/archieve_icon.svg';

import { useNavigate } from 'react-router-dom';

const TenantManagement = () => {
  // State management
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const [currentFormStep, setCurrentFormStep] = useState(1);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTenants, setTotalTenants] = useState(0);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false); // NEW: Success modal
  const [showPublicProfileModal, setShowPublicProfileModal] = useState(false); // NEW: Public profile modal
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [createdTenant, setCreatedTenant] = useState(null); // NEW: Store created tenant data

  // Form state - Split into basic and public profile
  const [basicFormData, setBasicFormData] = useState({
    primaryEmail: '',
    name: '',
    location: ''
  });
  
  const [publicProfileData, setPublicProfileData] = useState({
    address: '',
    description: '',
    yearEstablished: '',
    contactPhone: '',
    services: [''],
    appointmentTypes: ['']
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load tenants on component mount and when filters change
  useEffect(() => {
    loadTenants();
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, locationFilter]);

  // Load unique locations for filter dropdown
  useEffect(() => {
    loadUniqueLocations();
  }, [tenants]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (locationFilter !== 'all') params.location = locationFilter;
      
      // Call tenant service (we'll add this to adminService)
      const response = await adminService.getTenants(params);
      
      if (response.success) {
        setTenants(response.data || []);
        setTotalTenants(response.total || 0);
        setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
      } else {
        throw new Error(response.message || 'Failed to load tenants');
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError(err.message);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setTenants([
          {
            _id: '1',
            tenantId: 'NLX-2025-001',
            name: 'MindBridge Health',
            location: 'New York, NY',
            active: true,
            doctorCount: 15,
            patientCount: 320,
            createdAt: '2025-04-19T00:00:00Z'
          },
          {
            _id: '2',
            tenantId: 'NLX-2025-002',
            name: 'NovaTherapy',
            location: 'Los Angeles, CA',
            active: true,
            doctorCount: 4,
            patientCount: 53,
            createdAt: '2025-03-01T00:00:00Z'
          },
          {
            _id: '3',
            tenantId: 'NLX-2025-003',
            name: 'ClearPath Clinic',
            location: 'Chicago, IL',
            active: false,
            doctorCount: 8,
            patientCount: 129,
            createdAt: '2025-02-28T00:00:00Z'
          },
          {
            _id: '4',
            tenantId: 'NLX-2025-004',
            name: 'Serenity Center',
            location: 'Miami, FL',
            active: false,
            doctorCount: 10,
            patientCount: 182,
            createdAt: '2025-02-12T00:00:00Z'
          }
        ]);
        setTotalTenants(4);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUniqueLocations = () => {
    const uniqueLocations = [...new Set(tenants.map(tenant => tenant.location))].filter(Boolean);
    setLocations(uniqueLocations);
  };

  // Handle basic form field changes
  const handleBasicInputChange = (e) => {
    const { id, value } = e.target;
    setBasicFormData({ ...basicFormData, [id]: value });
  };

  // Handle public profile form field changes
  const handlePublicProfileInputChange = (e) => {
    const { id, value } = e.target;
    setPublicProfileData({ ...publicProfileData, [id]: value });
  };

  // Handle service list changes
  const handleServiceChange = (index, value) => {
    const newServices = [...publicProfileData.services];
    newServices[index] = value;
    setPublicProfileData({ ...publicProfileData, services: newServices });
  };

  // Add new service field
  const addService = () => {
    setPublicProfileData({ ...publicProfileData, services: [...publicProfileData.services, ''] });
  };

  // Remove service field
  const removeService = (index) => {
    const newServices = [...publicProfileData.services];
    newServices.splice(index, 1);
    setPublicProfileData({ ...publicProfileData, services: newServices });
  };

  // Handle appointment type changes
  const handleAppointmentTypeChange = (index, value) => {
    const newTypes = [...publicProfileData.appointmentTypes];
    newTypes[index] = value;
    setPublicProfileData({ ...publicProfileData, appointmentTypes: newTypes });
  };

  // Add new appointment type field
  const addAppointmentType = () => {
    setPublicProfileData({ ...publicProfileData, appointmentTypes: [...publicProfileData.appointmentTypes, ''] });
  };

  // Remove appointment type field
  const removeAppointmentType = (index) => {
    const newTypes = [...publicProfileData.appointmentTypes];
    newTypes.splice(index, 1);
    setPublicProfileData({ ...publicProfileData, appointmentTypes: newTypes });
  };

  // Handle basic tenant creation (Step 1)
  const handleBasicSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateBasicForm()) return;
  
  try {
    setSubmitting(true);
    
    const response = await adminService.createBasicTenant(basicFormData);
    
    if (response.success) {
      setCreatedTenant(response.data);
      setCurrentFormStep(2); // Move to confirmation step
      loadTenants();
    } else {
      throw new Error(response.message || 'Failed to create tenant');
    }
  } catch (err) {
    console.error('Error creating basic tenant:', err);
    toast.error(err.message);
  } finally {
    setSubmitting(false);
  }
};

  // Handle public profile creation (Step 2)
  const handlePublicProfileSubmit = async (e) => {
  e.preventDefault();
  
  if (!createdTenant) return;
  
  try {
    setSubmitting(true);
    
    const cleanedData = {
      ...publicProfileData,
      services: publicProfileData.services.filter(service => service.trim() !== ''),
      appointmentTypes: publicProfileData.appointmentTypes.filter(type => type.trim() !== '')
    };
    
    const response = await adminService.createTenantPublicProfile(createdTenant._id, cleanedData);
    
    if (response.success) {
      toast.success('Public profile created successfully!');
      setShowAddModal(false);
      setCurrentFormStep(1); // Reset to step 1
      resetAllForms();
      loadTenants();
    } else {
      throw new Error(response.message || 'Failed to create public profile');
    }
  } catch (err) {
    console.error('Error creating public profile:', err);
    toast.error(err.message);
  } finally {
    setSubmitting(false);
  }
};

  // Handle edit tenant submission (existing functionality)
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTenant) return;
    
    try {
      setSubmitting(true);
      
      // Combine basic and public profile data for edit
      const combinedData = {
        ...basicFormData,
        ...publicProfileData,
        services: publicProfileData.services.filter(service => service.trim() !== ''),
        appointmentTypes: publicProfileData.appointmentTypes.filter(type => type.trim() !== '')
      };
      
      const response = await adminService.updateTenant(selectedTenant._id, combinedData);
      
      if (response.success) {
        toast.success('Tenant updated successfully!');
        setShowEditModal(false);
        loadTenants();
        resetAllForms();
      } else {
        throw new Error(response.message || 'Failed to update tenant');
      }
    } catch (err) {
      console.error('Error updating tenant:', err);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Validate basic form data (only 3 required fields)
  const validateBasicForm = () => {
    const errors = {};
    
    if (!basicFormData.primaryEmail?.trim()) {
      errors.primaryEmail = 'Primary email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(basicFormData.primaryEmail)) {
      errors.primaryEmail = 'Please enter a valid email address';
    }
    
    if (!basicFormData.name?.trim()) {
      errors.name = 'Clinic name is required';
    }
    
    if (!basicFormData.location?.trim()) {
      errors.location = 'Clinic location is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset all form data
  const resetAllForms = () => {
    setBasicFormData({
      primaryEmail: '',
      name: '',
      location: ''
    });
    setPublicProfileData({
      address: '',
      description: '',
      yearEstablished: '',
      contactPhone: '',
      services: [''],
      appointmentTypes: ['']
    });
    setFormErrors({});
    setSelectedTenant(null);
    setCreatedTenant(null);
  };

  // Handle status toggle (active/inactive)
  const handleStatusToggle = async (tenant) => {
    try {
      const newStatus = !tenant.active;
      const response = await adminService.updateTenantStatus(tenant._id, { active: newStatus });
      
      if (response.success) {
        toast.success(`Tenant ${newStatus ? 'activated' : 'deactivated'} successfully`);
        loadTenants();
      } else {
        throw new Error(response.message || 'Failed to update tenant status');
      }
    } catch (err) {
      console.error('Error updating tenant status:', err);
      toast.error(err.message);
    }
  };

  // Handle tenant deletion
const handleDelete = async () => {
  if (!selectedTenant) return;
  
  try {
    // Call archive endpoint instead of delete
    const response = await adminService.archiveTenant(selectedTenant._id);
    
    if (response.success) {
      toast.success(`Tenant archived successfully. Will be deleted in 30 days.`);
      setShowDeleteModal(false);
      loadTenants();
    } else {
      throw new Error(response.message || 'Failed to archive tenant');
    }
  } catch (err) {
    console.error('Error archiving tenant:', err);
    toast.error(err.message);
  }
};

// Handle restore tenant
const handleRestore = async (tenant) => {
  if (!window.confirm(`Are you sure you want to restore "${tenant.name}"?`)) {
    return;
  }
  
  try {
    const response = await adminService.restoreTenant(tenant._id);
    
    if (response.success) {
      toast.success(`Tenant "${tenant.name}" has been restored successfully!`);
      loadTenants();
    } else {
      throw new Error(response.message || 'Failed to restore tenant');
    }
  } catch (err) {
    console.error('Error restoring tenant:', err);
    toast.error(err.message);
  }
};

// Handle permanent delete (for archived tenants)
const handlePermanentDelete = async (tenant) => {
  if (!window.confirm(
    `⚠️ WARNING: This will PERMANENTLY delete "${tenant.name}" and all its data right now.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?`
  )) {
    return;
  }
  
  try {
    const response = await adminService.permanentlyDeleteTenant(tenant._id);
    
    if (response.success) {
      toast.success(`Tenant "${tenant.name}" has been permanently deleted.`);
      loadTenants();
    } else {
      throw new Error(response.message || 'Failed to permanently delete tenant');
    }
  } catch (err) {
    console.error('Error permanently deleting tenant:', err);
    toast.error(err.message);
  }
};

  // Handle PDF export
  const handleExportPDF = async () => {
    try {
      const filters = {
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        location: locationFilter !== 'all' ? locationFilter : undefined
      };
      
      await adminService.exportTenantsToPdf(filters);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error('Failed to export PDF');
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Open modals
  const openAddModal = () => {
    resetAllForms();
    setShowAddModal(true);
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    
    // Populate forms with tenant data
    setBasicFormData({
      primaryEmail: tenant.adminEmail || '',
      name: tenant.name || '',
      location: tenant.location || ''
    });
    
    setPublicProfileData({
      address: tenant.address || '',
      description: tenant.description || '',
      yearEstablished: tenant.yearEstablished || '',
      contactPhone: tenant.contactPhone || '',
      services: tenant.services?.length ? tenant.services : [''],
      appointmentTypes: tenant.appointmentTypes?.length ? tenant.appointmentTypes : ['']
    });
    
    setShowEditModal(true);
  };

  const openDeleteModal = (tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  // Handle success modal actions
  const handleCreatePublicProfile = () => {
    setShowSuccessModal(false);
    setShowPublicProfileModal(true);
  };

  const handleSkipPublicProfile = () => {
    setShowSuccessModal(false);
    resetAllForms();
    toast.success(`Welcome email sent to ${basicFormData.primaryEmail}! Tenant created successfully.`);
  };

  // Handle detail view
const openDetailView = (tenant) => {
    navigate(`/admin/tenants/${tenant._id}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Get pagination info
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalTenants);

  if (loading && tenants.length === 0) {
    return (
      <div className="tm-container">
        <div className="tm-loading-container">
          <div className="tm-loading-spinner"></div>
          <p>Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tm-container">
      {/* Header */}
       <div className="tm-dashboard-header">
        <h1 className="tm-dashboard-title">Tenant Management</h1>
        <p className="tm-dashboard-subtitle">Centralized control for managing all clinics connected to the system.</p>
      </div>

      {/* Neurolex Tenants Title */}
      <div className="tm-neurolex-tenants-title">
        <h2>Neurolex Tenants</h2>
      </div>

      {/* Search and actions bar */}
       <div className="tm-admin-actions-bar">
        {/* Top row with search and add button */}
        <div className="tm-top-actions-row">
          <div className="tm-left-actions">
           <div className="tm-search-box">
            <img src={SearchIcon} alt="Search" className="tm-search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tm-search-input"
            />
          </div>
            
            <button className="tm-add-tenant-button" onClick={openAddModal}>
              <svg className="tm-button-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Add a Tenant</span>
            </button>
          </div>
          
        </div>
        
        {/* Bottom row with filters and export button */}
<div className="tm-bottom-actions-row">
  <div className="tm-filter-box">
    {/* Status filter */}
    <div className="tm-filter-item">
      <label htmlFor="tm-status-filter">Status</label>
      
      {/* ⬇️⬇️⬇️ REPLACE THIS SELECT ELEMENT ⬇️⬇️⬇️ */}
      <select 
        id="tm-status-filter" 
        value={statusFilter} 
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="all">All</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="archived">Archived</option> {/* 🆕 NEW */}
      </select>
      {/* ⬆️⬆️⬆️ END OF REPLACEMENT ⬆️⬆️⬆️ */}
    </div>
    
    <div className="tm-filter-item">
      <label htmlFor="tm-location-filter">Location</label>
              <select 
                id="tm-location-filter" 
                value={locationFilter} 
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="all">All</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Export button */}
          <button className="tm-export-pdf-button" onClick={handleExportPDF}>
            <svg className="tm-button-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" fill="currentColor"/>
              <path d="M14 2V8H20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Export PDF</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="tm-error-message">
          <p>Error: {error}</p>
          <button onClick={loadTenants} className="tm-btn-retry">Retry</button>
        </div>
      )}

      {/* Tenants table */}
{tenants.length === 0 ? (
  <div className="tm-no-data-message">
    <h3>No tenants found</h3>
    <p>Try adjusting your search or filters, or add a new tenant.</p>
  </div>
) : (
  <div className="tm-custom-table">
    <div className="tm-table-row tm-header-row">
      <div className="tm-table-cell">Clinic Name</div>
      <div className="tm-table-cell">Tenant ID</div>
      <div className="tm-table-cell">Status</div>
      <div className="tm-table-cell"># Doctors</div>
      <div className="tm-table-cell"># Patients</div>
      <div className="tm-table-cell">Created Date</div>
      <div className="tm-table-cell">Actions</div>
    </div>
    
    {tenants.map((tenant, index) => (
      <div 
        key={tenant._id} 
        className={`tm-table-row ${index % 2 === 0 ? 'tm-odd-row' : 'tm-even-row'}`}
      >
        <div className="tm-table-cell">
          <div className="tm-clinic-name-cell">
            <div className="tm-clinic-info">
              <div className="tm-clinic-name">
                {tenant.name}
              </div>
              <div className="tm-clinic-location">
                {tenant.location}
              </div>
            </div>
          </div>
        </div>
        <div className="tm-table-cell">
          <span className="tm-tenant-id-badge">
            {tenant.tenantId}
          </span>
        </div>
        <div className="tm-table-cell">
          <span className={`tm-status-badge ${tenant.active ? 'active' : 'inactive'}`}>
            {tenant.active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
        <div className="tm-table-cell">
          <span className="tm-count-badge">
            {tenant.doctorCount || 0}
          </span>
        </div>
        <div className="tm-table-cell">
          <span className="tm-count-badge">
            {tenant.patientCount || 0}
          </span>
        </div>
        <div className="tm-table-cell">{formatDate(tenant.createdAt)}</div>
        <div className="tm-table-cell">
          <div className="tm-action-buttons">
            <button 
              className="tm-btn-icon view"
              onClick={() => openDetailView(tenant)}
              title="View Details"
            >
              <div className="tm-view-icon"></div>
            </button>
            
            {tenant.status === 'archived' ? (
              <>
                <button 
                  className="tm-btn-icon enable" 
                  onClick={() => handleRestore(tenant)}
                  title="Restore Tenant"
                >
                  <div className="tm-enable-icon"></div>
                </button>
                <button 
                  className="tm-btn-icon delete"
                  onClick={() => handlePermanentDelete(tenant)}
                  title="Permanently Delete Now"
                >
                  <div className="tm-delete-icon"></div>
                </button>
              </>
            ) : (
              <>
                <button 
                  className={`tm-btn-icon ${tenant.active ? 'disable' : 'enable'}`}
                  onClick={() => handleStatusToggle(tenant)}
                  title={tenant.active ? 'Disable' : 'Enable'}
                >
                  <div className={tenant.active ? 'tm-disable-icon' : 'tm-enable-icon'}></div>
                </button>
                <button 
                  className="tm-btn-icon delete"
                  onClick={() => openDeleteModal(tenant)}
                  title="Archive Tenant"
                >
                  <img src={ArchiveIcon} alt="Archive" className="tm-action-icon" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    ))}
    
  </div>
)}

{/* Pagination */}
{totalPages > 1 && (
  <div className="tm-pagination">
    <button 
      className="tm-page-nav prev"
      onClick={() => handlePageChange(currentPage - 1)}
      disabled={currentPage === 1}
    >
      &lt;
    </button>
    
    <div className="tm-page-numbers">
      {[...Array(totalPages)].map((_, index) => (
        <button
          key={index}
          className={`tm-page-number ${currentPage === index + 1 ? 'active' : ''}`}
          onClick={() => handlePageChange(index + 1)}
        >
          {index + 1}
        </button>
      ))}
    </div>
    
    <button 
      className="tm-page-nav next"
      onClick={() => handlePageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
    >
      &gt;
    </button>
  </div>
)}

     {/* Multi-Step Tenant Creation Modal */}
{showAddModal && (
  <div className="tm-modal-overlay" onClick={() => {
    setShowAddModal(false);
    resetAllForms();
    setCurrentFormStep(1);
  }}>
    <div 
      className={`tm-modal-content tm-modal-with-sidebar ${currentFormStep === 2 ? 'tm-confirmation-step' : ''}`} 
      onClick={e => e.stopPropagation()}
      style={currentFormStep === 2 ? { maxWidth: '700px', width: '90%', borderRadius: '10px' } : {}}
    >
      {/* Logo/Brand Container - Hide on confirmation step */}
      {currentFormStep !== 2 && (
          <div className="tm-modal-sidebar">
            <div className="tm-modal-logo-container">
              <img src={NeuroLexLogo} alt="Neurolex Logo" className="tm-modal-logo" />
            </div>
          </div>
        )}

      {/* Form Container */}
      <div className="tm-modal-form-container">
        {/* Step 1: Basic Information */}
        {currentFormStep === 1 && (
          <>
            <div className="tm-modal-header">
              <h2>Clinic Tenant Setup</h2>
              <img src={TenantSetupIcon} alt="Tenant Setup" className="tm-modal-header-icon" />
            </div>
            
            <form onSubmit={handleBasicSubmit} className="tm-tenant-form">
              <div className="tm-form-group">
                <label htmlFor="primaryEmail">Primary Contact Email *</label>
                <input
                  type="email"
                  id="primaryEmail"
                  value={basicFormData.primaryEmail}
                  onChange={handleBasicInputChange}
                  className={formErrors.primaryEmail ? 'tm-error' : ''}
                  placeholder="admin@clinic.com"
                />
                {formErrors.primaryEmail && (
                  <span className="tm-error-text">{formErrors.primaryEmail}</span>
                )}
                <small>A welcome email will be sent to this address</small>
              </div>
              
              <div className="tm-form-group">
                <label htmlFor="name">Clinic Name *</label>
                <input
                  type="text"
                  id="name"
                  value={basicFormData.name}
                  onChange={handleBasicInputChange}
                  className={formErrors.name ? 'tm-error' : ''}
                  placeholder="Enter clinic name"
                />
                {formErrors.name && (
                  <span className="tm-error-text">{formErrors.name}</span>
                )}
                <small>This name will be used for the database and branding</small>
              </div>
              
              <div className="tm-form-group">
                <label htmlFor="location">Clinic Location *</label>
                <input
                  type="text"
                  id="location"
                  value={basicFormData.location}
                  onChange={handleBasicInputChange}
                  className={formErrors.location ? 'tm-error' : ''}
                  placeholder="City, State"
                />
                {formErrors.location && (
                  <span className="tm-error-text">{formErrors.location}</span>
                )}
                <small>The city and state where the clinic is located</small>
              </div>
              
              <div className="tm-form-actions">
                <button 
                  type="button" 
                  className="tm-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetAllForms();
                    setCurrentFormStep(1);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="tm-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </>
        )}

{/* Step 2: Success Confirmation */}
{currentFormStep === 2 && createdTenant && (
  <div style={{ 
    maxWidth: '600px',  // Adjust this value to your preferred width
    width: '100%',
    margin: '0 auto'    // Centers the modal content
  }}>
    <div className="tm-success-modal-header">
      <img src={NeuroLexLogo2} alt="Neurolex Logo" className="tm-success-modal-logo" />
      <h2 className="tm-success-modal-title">SUCCESSFUL CLINIC TENANT SETUP</h2>
    </div>
    
    <div className="tm-success-modal-body">
      <div className="tm-success-summary-box">
        <div className="tm-success-summary-row">
          <span className="tm-success-label">Tenant ID:</span>
          <span className="tm-success-value">{createdTenant.tenantId}</span>
        </div>
        <div className="tm-success-summary-row">
          <span className="tm-success-label">Clinic Name:</span>
          <span className="tm-success-value">{createdTenant.name}</span>
        </div>
        <div className="tm-success-summary-row">
          <span className="tm-success-label">Location:</span>
          <span className="tm-success-value">{createdTenant.location}</span>
        </div>
      </div>
    </div>
    
    <div className="tm-success-modal-actions">
      <button 
        className="tm-success-btn-skip"
        onClick={() => {
          setShowAddModal(false);
          resetAllForms();
          setCurrentFormStep(1);
          toast.success(`Welcome email sent to ${basicFormData.primaryEmail}! Tenant created successfully.`);
        }}
      >
        Skip for Now
      </button>
      <button 
        className="tm-success-btn-create"
        onClick={() => setCurrentFormStep(3)}
      >
        Create Public Profile
      </button>
    </div>
  </div>
)}
        {/* Step 3: Public Profile Form */}
        {currentFormStep === 3 && createdTenant && (
          <>
            <div className="tm-modal-header">
  <h2 style={{ fontSize: '35px', padding:'24px' }}>Create Public Profile for {createdTenant.name}</h2>
</div>
            
            <form onSubmit={handlePublicProfileSubmit} className="tm-tenant-form">
              <div className="tm-form-group">
                <label htmlFor="address">Clinic Address</label>
                <input
                  type="text"
                  id="address"
                  value={publicProfileData.address}
                  onChange={handlePublicProfileInputChange}
                  placeholder="Full street address"
                />
                <small>Complete street address of the clinic</small>
              </div>
              
              <div className="tm-form-group">
                <label htmlFor="description">Clinic Description</label>
                <textarea
                  id="description"
                  value={publicProfileData.description}
                  onChange={handlePublicProfileInputChange}
                  placeholder="Describe your clinic's specialties and focus"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    border: '1px solid #A7D7C5',
                    borderRadius: '10px',
                    backgroundColor: '#EEF7F4',
                    fontSize: '20px',
                    fontFamily: 'IBM Plex Mono',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
                <small>A brief description of the clinic's services and mission</small>
              </div>
                            
              <div className="tm-form-group">
                <label htmlFor="yearEstablished">Year Established</label>
                <input
                  type="number"
                  id="yearEstablished"
                  value={publicProfileData.yearEstablished}
                  onChange={handlePublicProfileInputChange}
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                />
                <small>Year when the clinic was established</small>
              </div>
              
              <div className="tm-form-group">
                <label htmlFor="contactPhone">Contact Number</label>
                <input
                  type="tel"
                  id="contactPhone"
                  value={publicProfileData.contactPhone}
                  onChange={handlePublicProfileInputChange}
                  placeholder="Clinic phone number"
                />
                <small>Main contact number for the clinic</small>
              </div>
              
              {/* Services section */}
              <div className="tm-form-group">
                <label>Services Offered</label>
                {publicProfileData.services.map((service, index) => (
                  <div key={`service-${index}`} className="tm-dynamic-input-row">
                    <input
                      type="text"
                      value={service}
                      onChange={(e) => handleServiceChange(index, e.target.value)}
                      placeholder="Enter a service"
                      className="tm-dynamic-input"
                    />
                    <div className="tm-dynamic-input-buttons">
                      {index > 0 && (
                        <button 
                          type="button" 
                          onClick={() => removeService(index)}
                          className="tm-btn-icon remove"
                        >
                          −
                        </button>
                      )}
                      {index === publicProfileData.services.length - 1 && (
                        <button 
                          type="button" 
                          onClick={addService}
                          className="tm-btn-icon add"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <small>List the services the clinic provides</small>
              </div>
              
              {/* Appointment Types section */}
              <div className="tm-form-group">
                <label>Appointment Types</label>
                {publicProfileData.appointmentTypes.map((type, index) => (
                  <div key={`appointment-${index}`} className="tm-dynamic-input-row">
                    <input
                      type="text"
                      value={type}
                      onChange={(e) => handleAppointmentTypeChange(index, e.target.value)}
                      placeholder="Enter an appointment type"
                      className="tm-dynamic-input"
                    />
                    <div className="tm-dynamic-input-buttons">
                      {index > 0 && (
                        <button 
                          type="button" 
                          onClick={() => removeAppointmentType(index)}
                          className="tm-btn-icon remove"
                        >
                          −
                        </button>
                      )}
                      {index === publicProfileData.appointmentTypes.length - 1 && (
                        <button 
                          type="button" 
                          onClick={addAppointmentType}
                          className="tm-btn-icon add"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <small>Types of appointments available at the clinic</small>
              </div>
              
              <div className="tm-form-actions">
                <button 
                  type="button" 
                  className="tm-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetAllForms();
                    setCurrentFormStep(1);
                  }}
                >
                  Skip for Now
                </button>
                <button 
                  type="submit" 
                  className="tm-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating Profile...' : 'Create Public Profile'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  </div>
)}

      {/* Edit Tenant Modal (combines both basic and public profile) */}
      {showEditModal && (
        <div className="tm-edit-modal-overlay" onClick={() => {
          setShowEditModal(false);
          resetAllForms();
        }}>
          <div className="tm-edit-modal-content tm-edit-large-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-edit-modal-header">
              <h2>Edit Tenant: {selectedTenant?.name}</h2>
              <button 
                className="tm-edit-modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  resetAllForms();
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="tm-edit-tenant-form">
              {/* Basic Information Section */}
              <div className="tm-edit-form-section">
                <h3>Basic Information</h3>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="primaryEmail">Primary Email *</label>
                  <input
                    type="email"
                    id="primaryEmail"
                    value={basicFormData.primaryEmail}
                    onChange={handleBasicInputChange}
                    className={formErrors.primaryEmail ? 'tm-error' : ''}
                    placeholder="admin@clinic.com"
                  />
                  {formErrors.primaryEmail && (
                    <span className="tm-error-text">{formErrors.primaryEmail}</span>
                  )}
                </div>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="name">Clinic Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={basicFormData.name}
                    onChange={handleBasicInputChange}
                    className={formErrors.name ? 'tm-error' : ''}
                    placeholder="Enter clinic name"
                  />
                  {formErrors.name && (
                    <span className="tm-error-text">{formErrors.name}</span>
                  )}
                </div>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="location">City *</label>
                  <input
                    type="text"
                    id="location"
                    value={basicFormData.location}
                    onChange={handleBasicInputChange}
                    className={formErrors.location ? 'tm-error' : ''}
                    placeholder="Enter city"
                  />
                  {formErrors.location && (
                    <span className="tm-error-text">{formErrors.location}</span>
                  )}
                </div>
              </div>

              {/* Public Profile Section */}
              <div className="tm-edit-form-section">
                <h3>Public Profile Information</h3>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="description">Clinic Description</label>
                  <textarea
                    id="description"
                    value={publicProfileData.description}
                    onChange={handlePublicProfileInputChange}
                    placeholder="Describe your clinic's specialties and focus"
                    rows="3"
                  />
                </div>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="address">Clinic Address</label>
                  <input
                    type="text"
                    id="address"
                    value={publicProfileData.address}
                    onChange={handlePublicProfileInputChange}
                    placeholder="Full street address"
                  />
                </div>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="yearEstablished">Year Established</label>
                  <input
                    type="number"
                    id="yearEstablished"
                    value={publicProfileData.yearEstablished}
                    onChange={handlePublicProfileInputChange}
                    placeholder="YYYY"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
                
                <div className="tm-edit-form-group">
                  <label htmlFor="contactPhone">Contact Number</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    value={publicProfileData.contactPhone}
                    onChange={handlePublicProfileInputChange}
                    placeholder="Clinic phone number"
                  />
                </div>
                
{/* Services section */}
<div className="tm-edit-form-group">
  <label>Services Offered</label>
  {publicProfileData.services.map((service, index) => (
    <div key={`service-${index}`} className="tm-dynamic-input-row">
      <input
        type="text"
        value={service}
        onChange={(e) => handleServiceChange(index, e.target.value)}
        placeholder="Enter a service"
        className="tm-dynamic-input"
      />
      {index === publicProfileData.services.length - 1 && (
        <button 
          type="button" 
          onClick={addService}
          className="tm-btn-icon add"
          title="Add Service"
        >
          +
        </button>
      )}
      {index > 0 && (
        <button 
          type="button" 
          onClick={() => removeService(index)}
          className="tm-btn-icon remove"
          title="Remove Service"
        >
          −
        </button>
      )}
    </div>
  ))}
</div>

{/* Appointment Types section */}
<div className="tm-edit-form-group">
  <label>Appointment Types</label>
  {publicProfileData.appointmentTypes.map((type, index) => (
    <div key={`appointment-${index}`} className="tm-dynamic-input-row">
      <input
        type="text"
        value={type}
        onChange={(e) => handleAppointmentTypeChange(index, e.target.value)}
        placeholder="Enter an appointment type"
        className="tm-dynamic-input"
      />
      {index === publicProfileData.appointmentTypes.length - 1 && (
        <button 
          type="button" 
          onClick={addAppointmentType}
          className="tm-btn-icon add"
          title="Add Appointment Type"
        >
          +
        </button>
      )}
      {index > 0 && (
        <button 
          type="button" 
          onClick={() => removeAppointmentType(index)}
          className="tm-btn-icon remove"
          title="Remove Appointment Type"
        >
          −
        </button>
      )}
    </div>
  ))}
</div>
</div>

<div className="tm-form-actions">
  <button 
    type="button" 
    className="tm-btn-secondary"
    onClick={() => {
      setShowEditModal(false);
      resetAllForms();
    }}
  >
    Cancel
  </button>
  <button 
    type="submit" 
    className="tm-btn-primary"
    disabled={submitting}
  >
    {submitting ? 'Updating...' : 'Update Tenant'}
  </button>
</div>
</form>
</div>
</div>
      )}

      {/* Delete/Archive Confirmation Modal */}
      {showDeleteModal && selectedTenant && (
        <div className="tm-archive-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="tm-archive-modal-content" onClick={e => e.stopPropagation()}>
            <div className="tm-archive-modal-header">
              <div className="tm-archive-header-content">
                <img src={NeuroLexLogo2} alt="Neurolex Logo" className="tm-archive-modal-logo" />
                <h2>MANAGE TENANT ACCESS</h2>
              </div>
            </div>
            
            <div className="tm-archive-modal-body">
              <p className="tm-archive-question">Are you sure you want to archive this tenant?</p>
              
              <div className="tm-archive-tenant-info">
                <div className="tm-archive-tenant-name">{selectedTenant.name}</div>
                <span className="tm-tenant-id-badge">{selectedTenant.tenantId}</span>
              </div>
              
              <div className="tm-archive-warning">
                <span className="tm-archive-warning-icon">⚠️</span>
                <p>This tenant will be archived and automatically deleted after 30 days. You can restore it within this period.</p>
              </div>
            </div>
            
            <div className="tm-archive-modal-actions">
              <button 
                className="tm-archive-btn-cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="tm-archive-btn-confirm"
                onClick={handleDelete}
              >
                Archive Tenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;